import { lazy, Suspense, useCallback, useEffect } from 'react'

import { Button, LoadingIndicator, Typography } from 'components/common'
import {
  selectApproval,
  setCurrentApproval,
  setCurrentApprovalLoading,
  setPracticeId,
} from 'components/features/Approval'
import { useAppDispatch, useAppSelector } from 'hooks'
import { t } from 'i18next'
import { Info, Link, MessageCircle, X } from 'react-feather'
import { useSearchParams } from 'react-router-dom'
import { Approval } from 'redux-store/models/practice'

import styles from './ApprovalSidebarPage.module.scss'
import { PracticeQueryParams } from '../../../../../constants/application-constants'
import { AvailableRevisions } from '../../../../../redux-store/models'
import { practicesApi, setAvailableRevisions } from '../../api'

const ApprovalStartPage = lazy(
  () =>
    import(
      'components/features/Approval/components/ApprovalStartPage/ApprovalStartPage'
    )
)
const OngoingApproval = lazy(
  () =>
    import(
      'components/features/Approval/components/OngoingApproval/OngoingApproval'
    )
)

interface Props {
  practiceId: string
  onActionClick: (page: string | null) => () => void
}

const ApprovalSidebarPage = ({
  onActionClick,
  practiceId,
}: Props): JSX.Element => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [httpGetApproval] =
    practicesApi.useLazyGetLatestApprovalByPracticeIdQuery()
  const { currentApproval, isCurrentApprovalLoading } =
    useAppSelector(selectApproval)

  const loadApprovalishRevision = useCallback(async () => {
    dispatch(setCurrentApprovalLoading(true))
    try {
      const { data } = await httpGetApproval(practiceId).unwrap()
      dispatch(setCurrentApproval(data ? (data as Approval) : null))
      dispatch(setCurrentApprovalLoading(false))
    } catch (err) {
      dispatch(setCurrentApproval(null))
      dispatch(setCurrentApprovalLoading(false))
      dispatch(
        setAvailableRevisions({
          approval: false,
        } as AvailableRevisions)
      )
      if (
        searchParams.get(PracticeQueryParams.REVISION_STATUS) === 'approval'
      ) {
        searchParams.set(PracticeQueryParams.REVISION_STATUS, 'draft')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [dispatch, httpGetApproval, practiceId, searchParams, setSearchParams])

  useEffect(() => {
    dispatch(setPracticeId(practiceId))

    return () => {
      setPracticeId(null)
    }
  }, [dispatch, practiceId])

  useEffect(() => {
    if (practiceId) {
      loadApprovalishRevision()
    }
  }, [dispatch, practiceId, loadApprovalishRevision])

  return (
    <div className={styles.root}>
      <div className={styles.pageActions}>
        <Button
          variant="subtle"
          size="x-small"
          icon={<MessageCircle />}
          aria-label="View comments"
          onClick={onActionClick('comments')}
        />
        <Button
          onClick={onActionClick('link')}
          variant="subtle"
          size="small"
          icon={<Link />}
        />
        <Button
          onClick={onActionClick('details')}
          variant="subtle"
          size="x-small"
          icon={<Info />}
        >
          {t('practice.details')}
        </Button>
      </div>

      <div className={styles.header}>
        <Typography className={styles.title} fontWeight="bold">
          {t('approvalDetail.approval')}
        </Typography>
        <Button
          className={styles.btnClose}
          size="x-small"
          variant="text"
          icon={<X />}
          iconLabel={t('metadata.closeAria') ?? ''}
          onClick={onActionClick(null)}
        />
      </div>

      {isCurrentApprovalLoading && <LoadingIndicator />}
      {!isCurrentApprovalLoading && (
        <div className={styles.page}>
          {currentApproval !== null ? (
            <Suspense fallback={<div />}>
              <OngoingApproval revisionStatus={currentApproval.status} />
            </Suspense>
          ) : (
            <Suspense fallback={<div />}>
              <ApprovalStartPage />
            </Suspense>
          )}
        </div>
      )}
    </div>
  )
}

export default ApprovalSidebarPage
